import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import HistoryRow from './HistoryRow';
import { RootState } from '../../../../store';
import { useSelector } from 'react-redux';

interface ExpandableTableProps {
    data: any;
    onMenuClick: (key: string, row: any) => void;
    expanded?: boolean;
}

const ExpandableTable: React.FC<ExpandableTableProps> = ({ data, onMenuClick, expanded }) => {
    const { t } = useTranslation();
    const [sortConfig, setSortConfig] = useState<{
        field: string;
        direction: 'asc' | 'desc';
    } | null>(null);
    const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(12);
    const planDetails = useSelector((state: RootState) => state.mainReducer.planDetails);
    
    let isLgOrDoveRunner = planDetails.account_id==="L3718483" ||planDetails.account_id==="99999999"

    const detailViewColumns = [
        { key: 'version', translationKey: 'version', isStaticValue: true, staticValue: '1' },
        { key: 'status_code', translationKey: 'status' },
        { key: 'service_type', translationKey: 'platform' },
        { key: 'core_version', translationKey: 'service_version' },
        { key: 'hybrid_platform', translationKey: 'framework' },
        { key: 'package_name', translationKey: 'package_name' },
        { key: 'app_name', translationKey: 'app_name' },
        { key: 'user_id', translationKey: 'user_id' },
        { key: 'original_file_name', translationKey: 'file_name' },
        { key: 'sealed_date', translationKey: 'sealed_date' },
        { key: 'sealingPresetName', translationKey: 'preset' },
        { key: 'option_mode', translationKey: 'sealing_mode' },
        { key: 'option_appType', translationKey: 'app_category' },
        { key: 'app_version', translationKey: 'app_version' },
        { key: 'option_dexEncrypt', translationKey: 'dex_encryption' },
        { key: 'dex_string_encrypt', translationKey: 'dex_string_encrypt' },
        { key: 'option_rooting', translationKey: 'rooting' },
        { key: 'option_emulator', translationKey: 'emulator' },
        { key: 'option_blockWorkProfile', translationKey: 'work_profile' },
        { key: 'option_macro', translationKey: 'macro_tools' },
        { key: 'option_sniff', translationKey: 'panel_attacks' },
        { key: 'option_blockKeyLogger', translationKey: 'keyloggers' },
        { key: 'option_dataSealing', translationKey: 'data_sealing' },
        { key: 'option_useOtaService', translationKey: 'over_the_air_update' },
        { key: 'block_usb_debugging', translationKey: 'usb_debugging' },
        { key: 'block_developer_options', translationKey: 'developer_options' },
        { key: 'block_screen_capture', translationKey: 'screen_capture' },
        { key: 'option_blockMirroring', translationKey: 'mirroring_app' },
        { key: 'option_blockRemoteAccess', translationKey: 'remote_access_app' },
        { key: 'hide_overlay_windows', translationKey: 'overlay_windows' },
        { key: 'protect_unity_metadata', translationKey: 'protect_unity_metadata' },
        { key: 'dex_string_obfuscation', translationKey: 'dex_string_obfuscation' },
        { key: 'dex_method_call_obfuscation', translationKey: 'dex_method_call_obfuscation' },
    ];

    const sortData = (data: any[]) => {
        if (!sortConfig) return data;

        return [...data].sort((a, b) => {
            let aValue = a[sortConfig.field];
            let bValue = b[sortConfig.field];

            if (typeof aValue === 'string' && aValue.includes('$')) {
                aValue = parseFloat(aValue.replace(/[^0-9.-]+/g, ''));
            }
            if (typeof bValue === 'string' && bValue.includes('$')) {
                bValue = parseFloat(bValue.replace(/[^0-9.-]+/g, ''));
            }

            const bothAreNumbers = typeof aValue === 'number' && typeof bValue === 'number';

            if (bothAreNumbers) {
                return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
            } else {
                aValue = String(aValue);
                bValue = String(bValue);
                return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }
        });
    };

    const handleSort = (field: string) => {
        const isAsc = sortConfig && sortConfig.field === field && sortConfig.direction === 'asc';
        setSortConfig({ field, direction: isAsc ? 'desc' : 'asc' });
    };

    const escapeCSVValue = (value: any): string => {
        if (value == null) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
    };

    const formatCSVValue = (row: any, column: any): string => {
        if (column.isStaticValue) {
            return column.staticValue || '';
        }
        const value = row[column.key];
        switch (column.key) {
            case 'status_code':
                if (value === '3') return t('failed');
                if (value === '2' || value === '') return t('completed');
                if (value === '1') return t('pending');
                if (value === '0') return t('running');
                return value || '';

            case 'service_type':
                return value === 'NATIVE_AOS' ? 'Native' : 'Hybrid';

            case 'sealed_date':
                return value ? moment(value, 'YYYYMMDDHHmmss').format('YYYY/MM/DD HH:mm:ss') : '';

            case 'option_appType':
                return row.core_version >= '3.0.0.0' ? '-' : value || '';

            case 'option_dexEncrypt':
                if (value?.toLowerCase() === 'yes' && row.option_selectDexEncrypt?.toLowerCase() === 'yes') {
                    return t('selective');
                }
                return t(value) || '';

            case 'option_rooting':
            case 'option_emulator':
            case 'option_macro':
            case 'option_sniff':
                return value ? t(value.toLowerCase()) : '';

            case 'option_blockWorkProfile':
            case 'option_blockKeyLogger':
                return value?.toLowerCase() === 'yes' || value === 'Block' ? t('block') : t('allow');

            case 'option_dataSealing':
                return value === 'native_api' ? t('yes') : t(value) || '';

            case 'block_usb_debugging':
            case 'block_developer_options':
            case 'block_screen_capture':
                return value?.toLowerCase() === 'yes' ? t('block') : t('allow');

            case 'hide_overlay_windows':
                return value?.toLowerCase() === 'yes' ? t('block') : t('allow');

            case 'dex_string_obfuscation':
            case 'dex_method_call_obfuscation':
                if (typeof value === 'object' && value) {
                    let result = t(value.enabled) || '';
                    if (value.enabled === 'yes' && value.level) {
                        result += `(${t(value.level)})`;
                    }
                    return result;
                }
                return t(value) || '';

            case 'package_name':
                return row.app_name ? `${value} (${row.app_name})` : value || '';

            default:
                return t(value) || '';
        }
    };

    const exportToCSV = () => {
        if (!data || data.length === 0) {
            alert(t('no_data_to_export') || 'No data to export');
            return;
        }

        const headers = detailViewColumns.map((col) => t(col.translationKey));
        const csvHeaders = headers.join(',');

        const csvRows = data.map((row: any) => {
            return detailViewColumns
                .map((col) => {
                    const formattedValue = formatCSVValue(row, col);
                    return escapeCSVValue(formattedValue);
                })
                .join(',');
        });

        const csvContent = [csvHeaders, ...csvRows].join('\n');

        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', 'Android_Sealing_history.csv');
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    
    const exportToUniqueAppCSV = () => {
        if (!data || data.length === 0) {
            alert(t('no_data_to_export') || 'No data to export');
            return;
        }

        var addedApps = [String]
        const headers = detailViewColumns.map((col) => t(col.translationKey));
        const csvHeaders = headers.join(',');


        const csvRows = data.map((row: any) => {
            if (addedApps.indexOf(row['package_name'])<0 ) {
                addedApps.push(row['package_name']);
            } else {
                return "";
            }
            return detailViewColumns
                .map((col) => {
                    const formattedValue = formatCSVValue(row, col);
                    return escapeCSVValue(formattedValue);
                })
                .join(',');
        }).filter((s:String) => s != "");

        const csvContent = [csvHeaders, ...csvRows].join('\n');

        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', 'Android_Sealing_history_per_app.csv');
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const tableHeaders = [
        {
            title: 'date',
            key: 'sealed_date',
        },
        {
            title: 'type',
            key: 'service_type',
        },
        {
            title: 'package_name',
            key: 'package_name',
        },
        {
            title: 'user_id',
            key: 'user_id',
        },
        {
            title: 'app_category',
            key: 'option_appType',
        },
        {
            title: 'version',
            key: 'core_version',
        },
        {
            title: 'status',
            key: 'status_code',
        },
    ];

    return (
        <div>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 0' }}>
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={exportToCSV}
                    disabled={!data || data.length === 0}
                    color="primary"
                >
                    {t('export') || 'EXPORT'}
                </Button>
                 {isLgOrDoveRunner ? (
                <Button sx={{ marginLeft:'10px'}}
                    variant="outlined"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={exportToUniqueAppCSV}
                    disabled={!data || data.length === 0}
                    color="secondary"
                >
                    {t('export latest by app') || 'EXPORT Latest by app'}
                </Button>

                ):null}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: 'none' }}>
                {data.length > 0 ? (
                    <>
                        <Table aria-label="historyical info table" sx={{ border: 'none' }} size="small">
                            <TableHead>
                                <TableRow sx={{ border: 'none' }}>
                                    {tableHeaders.map((header) => (
                                        <TableCell
                                            key={header.key}
                                            className="font-bold cursor-pointer"
                                            sortDirection={sortConfig?.field === header.key ? sortConfig.direction : false}
                                            onClick={() => handleSort(header.key)}
                                            onMouseEnter={() => setHoveredColumn(header.key)}
                                            onMouseLeave={() => setHoveredColumn(null)}
                                            sx={{ padding: '6px 14px' }}
                                        >
                                            <span>{t(header.title)}</span>
                                            <span className="inline-block min-w-[4] ml-2">
                                                {sortConfig && sortConfig.field === header.key ? (
                                                    sortConfig.direction === 'asc' ? (
                                                        <ArrowUpwardIcon fontSize="small" />
                                                    ) : (
                                                        <ArrowDownwardIcon fontSize="small" />
                                                    )
                                                ) : hoveredColumn === header.key ? (
                                                    <ArrowDownwardIcon fontSize="small" />
                                                ) : null}
                                            </span>
                                        </TableCell>
                                    ))}
                                    {!expanded && (
                                        <TableCell sx={{ padding: '6px 14px' }} className="font-bold">
                                            {t('action')}
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortData(data)
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row) => (
                                        <React.Fragment key={row.id}>
                                            <HistoryRow key={row.id} row={row} index={row.id} onMenuClick={onMenuClick} expanded={expanded} />
                                        </React.Fragment>
                                    ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            sx={{
                                paddingRight: '0',
                                '& .MuiToolbar-root': {
                                    paddingRight: '0 !important',
                                    paddingTop: '10px !important',
                                },
                            }}
                            component="div"
                            count={data.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
                            rowsPerPageOptions={[5, 10, 12, 20, 30]}
                        />
                    </>
                ) : (
                    <div className="mx-auto text-xl text-center whitespace-pre py-52">
                        <Typography variant="body1" sx={{ padding: '16px', textAlign: 'center' }}>
                            {t('no_data_to_show')}
                        </Typography>
                    </div>
                )}
            </TableContainer>
        </div>
    );
};

export default ExpandableTable;
